import { inject, ref, type Ref } from 'vue';
import { useStore } from 'vuex';
import { getAutomationPieces } from 'api/automation/automationService.js';
import type { PushToast, Translator } from 'js/shims-vue';

interface AutomationActionNode {
    settings?: { pieceName?: string };
    nextAction?: AutomationActionNode;
    children?: AutomationActionNode[];
    firstLoopAction?: AutomationActionNode;
}

interface AutomationWithPieces {
    version?: { trigger?: AutomationActionNode };
    pieces: AutomationPiece[];
}

export interface AutomationPiece {
    displayName: string;
    logoUrl: string;
    originalPieceName: string;
}

interface UseAutomationPieces {
    piecesList: Ref<AutomationPiece[]>;
    initializePiecesList: (automations: AutomationWithPieces[]) => Promise<void>;
}

export function useAutomationPieces(): UseAutomationPieces {
    const store = useStore();
    const $t = inject('$t') as Translator;
    const $pushToast = inject('$pushToast') as PushToast;

    const piecesList = ref<AutomationPiece[]>([]);

    const extractPieceNames = (action: AutomationActionNode|null|undefined, pieceNames: Set<string>): void => {
        if(!action){
            return;
        }
        
        if (action.settings?.pieceName) {
            pieceNames.add(action.settings.pieceName);
        }

        if (action.nextAction) {
            extractPieceNames(action.nextAction, pieceNames);
        }

        if (Array.isArray(action.children)) {
            for (const child of action.children) {
                extractPieceNames(child, pieceNames);
            }
        }

        if (action.firstLoopAction) {
            extractPieceNames(action.firstLoopAction, pieceNames);
        }
    };

    const getUniquePieceNames = (flows: AutomationWithPieces[]): string[] => {
        const pieceNames = new Set<string>();

        for (const flow of flows) {
            const trigger = flow.version?.trigger;
            if (trigger) {
                extractPieceNames(trigger, pieceNames);
            }
        }
        return Array.from(pieceNames);
    };

    const getPieceNamesForAutomation = (automation: AutomationWithPieces): string[] => {
        const pieceNames = new Set<string>();
        const trigger = automation.version?.trigger;
        if (trigger) {
            extractPieceNames(trigger, pieceNames);
        }
        return Array.from(pieceNames);
    };

    const fetchAutomationPieces = async (pieceNames: string[]): Promise<AutomationPiece[]> => {
        const cleanedPieceNames = pieceNames.map((name) => {
            if (name.startsWith('@activepieces/')) {
                return name.replace('@activepieces/', '');
            }
            return name;
        });

        try {
            const response = await getAutomationPieces(cleanedPieceNames);
            const piecesData = response.data;
            return pieceNames.flatMap((originalPieceName, index) => {
                const pieceData = piecesData[cleanedPieceNames[index]];
                if (!pieceData) {
                    return [];
                }
                return [
                    {
                        displayName: pieceData.displayName,
                        logoUrl: pieceData.logoUrl,
                        originalPieceName,
                    },
                ];
            });
        } catch (error) {
            store.state.system.errorsHandler.logException(error);
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                close: true,
                text: $t.__('common.labels.toast.error'),
            });
            return [];
        }
    };

    const initializePiecesList = async (automations: AutomationWithPieces[]): Promise<void> => {
        const pieces = getUniquePieceNames(automations);
        if (pieces.length === 0) {
            piecesList.value = [];
            return;
        }
        try {
            const results = await fetchAutomationPieces(pieces);
            piecesList.value = results;
            for (const automation of automations) {
                const automationPieceNames = getPieceNamesForAutomation(automation);
                automation.pieces = automationPieceNames
                    .map((pieceName) =>
                        piecesList.value.find(
                            (piece) => piece && piece.originalPieceName === pieceName,
                        ),
                    )
                    .filter((piece): piece is AutomationPiece => Boolean(piece));
            }
        } catch {
            piecesList.value = [];
        }
    };

    return {
        piecesList,
        initializePiecesList,
    };
}
